<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('attendance.add_attendance_adjustment') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form ref="formRef" :loading="isLoading" @submit-valid="onSave">
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <timezone-autocomplete
              v-model="addAttendanceAdjustment.punchInTimezoneName"
              :rules="rules.punchInTimezone"
              :label="
                $t('attendance.attendance_adjustment_punchInTimezoneName')
              "
              disabled
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="addAttendanceAdjustment.punchInUserDate"
              type="date"
              :rules="rules.punchInUserDate"
              :label="$t('attendance.attendance_adjustment_punchInUserDate')"
              :disabled="
                isParam && addAttendanceAdjustment.attendanceMode === 'in'
              "
              @update:model-value="revalidateForm"
              @keydown.enter.prevent
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="addAttendanceAdjustment.punchInUserTime"
              type="time"
              :rules="rules.punchInUserTime"
              :label="$t('attendance.attendance_adjustment_punchInUserTime')"
              :disabled="
                isParam && addAttendanceAdjustment.attendanceMode === 'in'
              "
              @update:model-value="revalidateForm"
              @keydown.enter.prevent
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <timezone-autocomplete
              v-model="addAttendanceAdjustment.punchOutTimezoneName"
              :rules="rules.punchOutTimezone"
              :label="
                $t('attendance.attendance_adjustment_punchOutTimezoneName')
              "
              disabled
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="addAttendanceAdjustment.punchOutUserDate"
              type="date"
              :rules="rules.punchOutUserDate"
              :label="$t('attendance.attendance_adjustment_punchOutUserDate')"
              :disabled="
                isParam && addAttendanceAdjustment.attendanceMode === 'out'
              "
              @update:model-value="revalidateForm"
              @keydown.enter.prevent
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="addAttendanceAdjustment.punchOutUserTime"
              type="time"
              :rules="rules.punchOutUserTime"
              :label="$t('attendance.attendance_adjustment_punchOutUserTime')"
              :disabled="
                isParam && addAttendanceAdjustment.attendanceMode === 'out'
              "
              @update:model-value="revalidateForm"
              @keydown.enter.prevent
            />
          </oxd-grid-item>
        </oxd-grid>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="addAttendanceAdjustment.note"
              type="textarea"
              :rules="rules.note"
              :label="$t('general.comment')"
              @keydown.enter.prevent
            />
          </oxd-grid-item>
        </oxd-grid>

        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
    <confirmation-dialog
      ref="existingRecordDialog"
      :title="existingRecordDialogTitle"
      :subtitle="existingRecordDialogSubtitle"
      :cancel-label="$t('general.no_cancel')"
      :confirm-label="$t('general.confirm')"
      icon="info-circle"
      confirm-button-type="secondary"
    />
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {
  formatDate,
  formatTime,
  parseDate,
  parseTime,
} from '@ohrm/core/util/helper/datefns';
import {APIService} from '@ohrm/core/util/services/api.service';
import moment from 'moment-timezone';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useLocale from '@/core/util/composable/useLocale';
import TimezoneAutocomplete from '../components/TimezoneAutocomplete.vue';
import ConfirmationDialog from '@ohrm/components/dialogs/ConfirmationDialog';
import {
  required,
  shouldNotExceedCharLength,
  validSelection,
  mustNotContainDisallowedNoteLiterals,
} from '@/core/util/validation/rules';
import {ref} from 'vue';
import {
  isWorkDurationWithinLimit,
  MAX_WORK_DURATION_ERROR_MESSAGE,
} from '../util/maxWorkDuration';
import {
  hasCompletePunchPair,
  validatePunchDateTimePair,
} from '../util/punchFieldValidation';

const addAttendanceAdjustmentModel = {
  attendanceMode: '',
  employeeAttendanceId: 0,
  note: '',
  punchInTimezoneName: ref(null),
  punchInUserTime: '',
  punchOutTimezoneName: ref(null),
  punchInUserDate: '',
  punchOutUserDate: '',
  punchInUserTimeOriginal: '',
  punchOutUserTimeOriginal: '',
  punchOutUserTime: '',
};
export default {
  components: {
    'timezone-autocomplete': TimezoneAutocomplete,
    'confirmation-dialog': ConfirmationDialog,
  },
  props: {
    notesDisallowedLiterals: {
      type: Array,
      default: () => [],
    },
    maxWorkDurationHours: {
      type: Number,
      default: null,
    },
  },
  setup() {
    const {locale} = useLocale();
    const {userDateFormat, timeFormat} = useDateFormat();
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/adjust',
    );
    const httpGet = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/my/attendance/records',
    );
    return {
      required,
      http,
      httpGet,
      locale,
      userDateFormat,
      timeFormat,
    };
  },
  data() {
    return {
      isLoading: false,
      addAttendanceAdjustment: {...addAttendanceAdjustmentModel},
      isParam: false,
      preservedPunchIn: null,
      preservedPunchOut: null,
      existingRecordDialogTitle: 'Existing attendance found',
      existingRecordDialogSubtitle: '',
    };
  },
  computed: {
    rules() {
      const requireWhen = (shouldApply) => (shouldApply ? [required] : []);
      const punchInPairRule = () =>
        validatePunchDateTimePair(
          this.getEffectivePunchInDate(),
          this.getEffectivePunchInTime(),
        );
      const punchOutPairRule = () =>
        validatePunchDateTimePair(
          this.getEffectivePunchOutDate(),
          this.getEffectivePunchOutTime(),
        );

      return {
        note: [
          required,
          shouldNotExceedCharLength(250),
          mustNotContainDisallowedNoteLiterals(this.notesDisallowedLiterals),
        ],
        punchInUserDate: this.preservedPunchIn
          ? []
          : [...requireWhen(this.requiresPunchInFields), punchInPairRule],
        punchOutUserDate: this.preservedPunchOut
          ? []
          : [...requireWhen(this.requiresPunchOutFields), punchOutPairRule],
        punchInUserTime: this.preservedPunchIn
          ? []
          : [...requireWhen(this.requiresPunchInFields), punchInPairRule],
        punchOutUserTime: this.preservedPunchOut
          ? []
          : [...requireWhen(this.requiresPunchOutFields), punchOutPairRule],
        punchInTimezone: this.requiresPunchInFields
          ? [required, validSelection]
          : [],
        punchOutTimezone: this.requiresPunchOutFields
          ? [required, validSelection]
          : [],
      };
    },
    requiresPunchInFields() {
      if (this.preservedPunchIn) {
        return false;
      }
      return !this.hasCompletePunchOut;
    },
    requiresPunchOutFields() {
      if (this.preservedPunchOut) {
        return false;
      }
      return !this.hasCompletePunchIn;
    },
    hasCompletePunchIn() {
      if (this.preservedPunchIn) {
        return true;
      }
      return hasCompletePunchPair(
        this.getEffectivePunchInDate(),
        this.getEffectivePunchInTime(),
      );
    },
    hasCompletePunchOut() {
      if (this.preservedPunchOut) {
        return true;
      }
      return hasCompletePunchPair(
        this.getEffectivePunchOutDate(),
        this.getEffectivePunchOutTime(),
      );
    },
  },
  created() {
    this.addAttendanceAdjustment.punchInTimezoneName = this.getUtcOffsetLabel(
      moment.tz.guess(),
    );
    this.addAttendanceAdjustment.punchOutTimezoneName = this.getUtcOffsetLabel(
      moment.tz.guess(),
    );

    const params = new URLSearchParams(window.location.search);
    const employeeAttendanceId = params.get('employeeAttendanceId');
    if (employeeAttendanceId) {
      this.loadAttendanceRecord(Number(employeeAttendanceId));
      return;
    }

    const attendanceMode = params.get('attendanceMode');
    if (attendanceMode) {
      this.isParam = true;
      const punchInUserDate = params.get('punchInUserDate');
      const punchInUserTime = decodeURIComponent(
        params.get('punchInUserTime') || '',
      );
      const punchOutUserDate = params.get('punchOutUserDate');
      const punchOutUserTime = decodeURIComponent(
        params.get('punchOutUserTime') || '',
      );
      this.addAttendanceAdjustment.attendanceMode = attendanceMode;
      if (attendanceMode === 'in') {
        const punchInDate = this.toFormDate(punchInUserDate);
        const punchInTime = this.toFormTime(punchInUserTime);
        this.addAttendanceAdjustment.punchInUserDate = punchInDate;
        this.addAttendanceAdjustment.punchInUserTime = punchInTime;
        if (punchInDate && punchInTime) {
          this.preservedPunchIn = {date: punchInDate, time: punchInTime};
        }
      } else if (attendanceMode === 'out') {
        const punchOutDate = this.toFormDate(punchOutUserDate);
        const punchOutTime = this.toFormTime(punchOutUserTime);
        this.addAttendanceAdjustment.punchOutUserDate = punchOutDate;
        this.addAttendanceAdjustment.punchOutUserTime = punchOutTime;
        if (punchOutDate && punchOutTime) {
          this.preservedPunchOut = {date: punchOutDate, time: punchOutTime};
        }
      }
    }
  },

  methods: {
    revalidateForm() {
      this.$nextTick(() => {
        this.$refs.formRef?.validate?.();
      });
    },
    getEffectivePunchInDate() {
      return (
        this.preservedPunchIn?.date ||
        this.addAttendanceAdjustment.punchInUserDate
      );
    },
    getEffectivePunchInTime() {
      return this.normalizeTimeValue(
        this.preservedPunchIn?.time ||
          this.addAttendanceAdjustment.punchInUserTime,
      );
    },
    getEffectivePunchOutDate() {
      return (
        this.preservedPunchOut?.date ||
        this.addAttendanceAdjustment.punchOutUserDate
      );
    },
    getEffectivePunchOutTime() {
      return this.normalizeTimeValue(
        this.preservedPunchOut?.time ||
          this.addAttendanceAdjustment.punchOutUserTime,
      );
    },
    normalizeTimeValue(value) {
      if (!value) {
        return '';
      }
      if (value instanceof Date) {
        return formatTime(value, 'HH:mm', {locale: this.locale});
      }
      return this.toFormTime(String(value));
    },
    resolveTimezoneName(submissionValue, punchValue) {
      return submissionValue || punchValue || moment.tz.guess();
    },
    toFormDate(dateString) {
      if (!dateString) {
        return '';
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      const parsed = parseDate(dateString, this.userDateFormat);
      return parsed
        ? formatDate(parsed, 'yyyy-MM-dd', {locale: this.locale})
        : '';
    },
    toFormTime(timeString) {
      if (!timeString) {
        return '';
      }
      const normalized = timeString.replace('.', ':');
      const parsed = parseTime(normalized, this.timeFormat);
      return parsed
        ? formatTime(parsed, 'HH:mm', {locale: this.locale})
        : normalized;
    },
    getPunchDateTimeFromRecord(punch, timezoneName) {
      if (!punch) {
        return {date: '', time: ''};
      }
      if (punch.userDateYmd && punch.userTime) {
        return {
          date: punch.userDateYmd,
          time: this.toFormTime(punch.userTime),
        };
      }
      if (punch.utcDateTime && timezoneName) {
        const local = moment(punch.utcDateTime).tz(timezoneName);
        if (local.isValid()) {
          return {
            date: local.format('YYYY-MM-DD'),
            time: local.format('HH:mm'),
          };
        }
      }
      if (punch.utcDate && punch.utcTime) {
        const utcDateTime = moment.utc(
          `${punch.utcDate} ${punch.utcTime}`,
          ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD HH:mm:ss', 'DD-MM-YYYY HH:mm'],
          true,
        );
        if (utcDateTime.isValid()) {
          const local = utcDateTime.tz(timezoneName);
          return {
            date: local.format('YYYY-MM-DD'),
            time: local.format('HH:mm'),
          };
        }
      }
      return {
        date: this.toFormDate(punch.userDate),
        time: this.toFormTime(punch.userTime),
      };
    },
    loadAttendanceRecord(attendanceId) {
      this.isLoading = true;
      this.httpGet
        .get(attendanceId)
        .then((response) => {
          const record = response.data.data;
          const punchInTimezone = this.resolveTimezoneName(
            record.submission?.punchInTimezoneName,
            record.punchIn?.timezoneName,
          );
          const punchOutTimezone = this.resolveTimezoneName(
            record.submission?.punchOutTimezoneName,
            record.punchOut?.timezoneName || punchInTimezone,
          );
          const punchIn = this.getPunchDateTimeFromRecord(
            record.punchIn,
            punchInTimezone,
          );
          const punchOut = this.getPunchDateTimeFromRecord(
            record.punchOut,
            punchOutTimezone,
          );
          this.addAttendanceAdjustment = {
            ...this.addAttendanceAdjustment,
            employeeAttendanceId: attendanceId,
            note: record.submission?.note || '',
            punchInTimezoneName: this.getUtcOffsetLabel(punchInTimezone),
            punchOutTimezoneName: this.getUtcOffsetLabel(punchOutTimezone),
            punchInUserDate: punchIn.date,
            punchInUserTime: punchIn.time,
            punchOutUserDate: punchOut.date,
            punchOutUserTime: punchOut.time,
          };
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getUtcOffsetLabel(timezone) {
      const offset = moment.tz(timezone).format('Z');
      return {
        id: timezone,
        label: `(UTC${offset}) ${timezone}`,
      };
    },
    convertToUtcIso(dateString, timeString, timeZoneName) {
      if (!dateString || !timeString || !timeZoneName) return null;
      const localDateTime = `${dateString}T${timeString}`;
      return moment.tz(localDateTime, timeZoneName).utc().format();
    },
    convertToUserTime(dateString, timeString, timeZoneName) {
      if (!dateString || !timeString || !timeZoneName) return null;
      const localDateTime = `${dateString}T${timeString}`;
      return moment.tz(localDateTime, timeZoneName).format();
    },
    convertUtcToLocalTime(utcTime, timeZoneName) {
      if (!utcTime || !timeZoneName) return '';
      return moment(utcTime).tz(timeZoneName).format('HH:mm');
    },
    convertUtcToLocalDate(utcTime, timeZoneName) {
      if (!utcTime || !timeZoneName) return '';
      return moment(utcTime).tz(timeZoneName).format('YYYY-MM-DD');
    },
    assertNotFuture(dateString, timeString, timeZoneName) {
      if (!dateString || !timeString || !timeZoneName) {
        return true;
      }
      const localDateTime = `${dateString}T${timeString}`;
      return moment.tz(localDateTime, timeZoneName).isSameOrBefore(moment());
    },
    getLookupDateRange() {
      const dates = [
        this.getEffectivePunchInDate(),
        this.getEffectivePunchOutDate(),
      ].filter(Boolean);
      if (!dates.length) {
        return null;
      }
      const sorted = [...dates].sort();
      return {fromDate: sorted[0], toDate: sorted[sorted.length - 1]};
    },
    pickExistingAttendanceRecord(records, range) {
      const punchInDate = this.getEffectivePunchInDate();
      const punchOutDate = this.getEffectivePunchOutDate();

      if (punchInDate) {
        const match = records.find(
          (record) => record.punchIn?.userDateYmd === punchInDate,
        );
        if (match) {
          return match;
        }
      }

      if (punchOutDate) {
        const match = records.find(
          (record) => record.punchOut?.userDateYmd === punchOutDate,
        );
        if (match) {
          return match;
        }
      }

      if (range.fromDate === range.toDate && records.length === 1) {
        return records[0];
      }

      return records[0] ?? null;
    },
    async findExistingAttendanceRecord() {
      const range = this.getLookupDateRange();
      if (!range) {
        return null;
      }

      try {
        const response = await this.httpGet.getAll({
          fromDate: range.fromDate,
          toDate: range.toDate,
          limit: 50,
        });
        const records = response?.data?.data ?? [];
        if (!records.length) {
          return null;
        }
        return this.pickExistingAttendanceRecord(records, range);
      } catch {
        return null;
      }
    },
    formatExistingRecordSummary(record) {
      const punchIn = record.punchIn;
      const punchOut = record.punchOut;
      const inText =
        punchIn?.userDate && punchIn?.userTime
          ? `${punchIn.userDate} ${punchIn.userTime}`
          : '-';
      const outText =
        punchOut?.userDate && punchOut?.userTime
          ? `${punchOut.userDate} ${punchOut.userTime}`
          : '-';
      return `Attendance already exists on this date (${inText} – ${outText}). Submit this adjustment for that record?`;
    },
    validateBeforeSave() {
      if (!this.hasCompletePunchIn && !this.hasCompletePunchOut) {
        this.$toast.show({
          type: 'error',
          title: this.$t('general.error'),
          message: 'Provide clock in or clock out date and time',
        });
        return false;
      }

      if (
        this.hasCompletePunchIn &&
        !this.assertNotFuture(
          this.getEffectivePunchInDate(),
          this.getEffectivePunchInTime(),
          this.addAttendanceAdjustment.punchInTimezoneName?.id,
        )
      ) {
        this.$toast.show({
          type: 'error',
          title: this.$t('general.error'),
          message: 'Clock in date and time cannot be in the future',
        });
        return false;
      }

      if (
        this.hasCompletePunchOut &&
        !this.assertNotFuture(
          this.getEffectivePunchOutDate(),
          this.getEffectivePunchOutTime(),
          this.addAttendanceAdjustment.punchOutTimezoneName?.id,
        )
      ) {
        this.$toast.show({
          type: 'error',
          title: this.$t('general.error'),
          message: 'Clock out date and time cannot be in the future',
        });
        return false;
      }

      if (
        this.maxWorkDurationHours !== null &&
        this.hasCompletePunchIn &&
        this.hasCompletePunchOut &&
        !isWorkDurationWithinLimit(
          this.getEffectivePunchInDate(),
          this.getEffectivePunchInTime(),
          this.addAttendanceAdjustment.punchInTimezoneName.id,
          this.getEffectivePunchOutDate(),
          this.getEffectivePunchOutTime(),
          this.addAttendanceAdjustment.punchOutTimezoneName.id,
          this.maxWorkDurationHours,
        )
      ) {
        this.$toast.show({
          type: 'error',
          title: this.$t('general.error'),
          message: MAX_WORK_DURATION_ERROR_MESSAGE,
        });
        return false;
      }

      return true;
    },
    buildAdjustmentPayload() {
      const payload = {
        note: this.addAttendanceAdjustment.note,
      };

      if (this.hasCompletePunchIn) {
        payload.punchInUserTime = this.convertToUserTime(
          this.getEffectivePunchInDate(),
          this.getEffectivePunchInTime(),
          this.addAttendanceAdjustment.punchInTimezoneName.id,
        );
        payload.punchInTimezoneName =
          this.addAttendanceAdjustment.punchInTimezoneName.id;
      }

      if (this.hasCompletePunchOut) {
        payload.punchOutUserTime = this.convertToUserTime(
          this.getEffectivePunchOutDate(),
          this.getEffectivePunchOutTime(),
          this.addAttendanceAdjustment.punchOutTimezoneName.id,
        );
        payload.punchOutTimezoneName =
          this.addAttendanceAdjustment.punchOutTimezoneName.id;
      }

      if (this.addAttendanceAdjustment.employeeAttendanceId) {
        payload.employeeAttendanceId =
          this.addAttendanceAdjustment.employeeAttendanceId;
      }

      return payload;
    },
    submitAdjustment() {
      this.isLoading = true;
      return this.http
        .create(this.buildAdjustmentPayload())
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.onCancel();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async onSave() {
      if (!this.validateBeforeSave()) {
        return;
      }

      if (!this.addAttendanceAdjustment.employeeAttendanceId) {
        const existingRecord = await this.findExistingAttendanceRecord();
        if (existingRecord) {
          if (existingRecord.submission?.status === 1) {
            this.$toast.show({
              type: 'error',
              title: this.$t('general.error'),
              message:
                'An adjustment is already pending for the attendance on this date. Edit the existing request instead.',
            });
            return;
          }

          this.existingRecordDialogSubtitle =
            this.formatExistingRecordSummary(existingRecord);
          const confirmation =
            await this.$refs.existingRecordDialog.showDialog();
          if (confirmation !== 'ok') {
            return;
          }
          this.addAttendanceAdjustment.employeeAttendanceId = existingRecord.id;
        }
      }

      await this.submitAdjustment();
    },
    onCancel() {
      navigate('/attendance/viewMyAttendanceAdjustment');
    },
  },
};
</script>
